import React, { useEffect, useRef, useState, useCallback } from 'react';
import './DevtoolConsole.scss';
import { useDevtoolStore } from '../../store/devtoolSlice';
import { useLogStore, LogEntry, LogLevel } from '../../store/logSlice';
import { useDevtoolDraggable } from '../../hooks/useDevtoolDraggable';
import { executeCommand } from '../../services/CommandService';
import { logger } from '../../services/LoggerService';

export const DevtoolConsole: React.FC = () => {
  const position = useDevtoolStore((s) => s.consolePosition);
  const setPosition = useDevtoolStore((s) => s.setConsolePosition);
  const size = useDevtoolStore((s) => s.consoleSize);
  const setSize = useDevtoolStore((s) => s.setConsoleSize);
  const entries = useLogStore((s) => s.entries);
  const clearEntries = useLogStore((s) => s.clear);
  const [enabledLevels, setEnabledLevels] = useState<Record<LogLevel, boolean>>(
    {
      log: true,
      info: true,
      warn: true,
      error: true,
    },
  );
  const visibleEntries = entries.filter((e) => enabledLevels[e.level]);

  const rootRef = useRef<HTMLDivElement>(null);
  const logBodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [draft, setDraft] = useState('');
  const [, setHistory] = useState<string[]>([]);
  const [historyIdx, setHistoryIdx] = useState<number>(-1);

  const { onPointerDown } = useDevtoolDraggable({
    position,
    setPosition,
    getSize: () => ({ width: size.width, height: size.height }),
  });

  useEffect(() => {
    const body = logBodyRef.current;
    if (body) {
      body.scrollTop = body.scrollHeight;
    }
  }, [entries]);

  // The native CSS resize handle emits no event, so a ResizeObserver is used to
  // persist the size when the user drags it.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) {
      return;
    }
    let last = { w: size.width, h: size.height };
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const w = Math.round(e.contentRect.width);
        const h = Math.round(e.contentRect.height);
        if (w !== last.w || h !== last.h) {
          last = { w, h };
          setSize({ width: w, height: h });
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [setSize, size.height, size.width]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const line = draft.trim();
        if (!line) {
          return;
        }
        logger.log(`> ${line}`);
        setHistory((h) => [...h, line]);
        setHistoryIdx(-1);
        setDraft('');
        executeCommand(line);
      } else if (e.key === 'Escape') {
        inputRef.current?.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHistory((h) => {
          if (h.length === 0) {
            return h;
          }
          const next =
            historyIdx < 0 ? h.length - 1 : Math.max(0, historyIdx - 1);
          setHistoryIdx(next);
          setDraft(h[next] ?? '');
          return h;
        });
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHistory((h) => {
          if (h.length === 0 || historyIdx < 0) {
            return h;
          }
          const next = historyIdx + 1;
          if (next >= h.length) {
            setHistoryIdx(-1);
            setDraft('');
          } else {
            setHistoryIdx(next);
            setDraft(h[next] ?? '');
          }
          return h;
        });
      }
    },
    [draft, historyIdx],
  );

  return (
    <div
      ref={rootRef}
      className="devtool-console"
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
      }}
    >
      <div className="devtool-console-header" onPointerDown={onPointerDown}>
        <span>Console</span>
        <div className="devtool-console-filters">
          {(['log', 'info', 'warn', 'error'] as LogLevel[]).map((level) => (
            <label
              key={level}
              className={`devtool-console-filter devtool-console-filter--${level}`}
            >
              <input
                type="checkbox"
                checked={enabledLevels[level]}
                onChange={(e) =>
                  setEnabledLevels((prev) => ({
                    ...prev,
                    [level]: e.target.checked,
                  }))
                }
              />
              {level}
            </label>
          ))}
        </div>
        <button
          className="devtool-console-clear"
          onClick={() => clearEntries()}
        >
          clear
        </button>
      </div>
      <div ref={logBodyRef} className="devtool-console-body">
        {visibleEntries.map((entry: LogEntry) => (
          <div
            key={entry.id}
            className={`devtool-console-line devtool-console-line--${entry.level}`}
          >
            {entry.message}
          </div>
        ))}
      </div>
      <div className="devtool-console-input-row">
        <span className="devtool-console-prompt">&gt;</span>
        <input
          ref={inputRef}
          className="devtool-console-input"
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="/give <item-id> [qty]"
          spellCheck={false}
          autoComplete="off"
        />
      </div>
    </div>
  );
};
